import { useCallback, useEffect, useRef, useState } from "react";

import {
  VOICE_ORDER_AUDIO_BITS_PER_SECOND,
  VOICE_ORDER_MAX_DURATION_MS,
  classifyGetUserMediaError,
  isRecordingSupported,
  pickAudioMimeType,
  type RecorderErrorKind
} from "@/lib/voice-order";

export type RecorderStatus = "idle" | "requesting-permission" | "recording";

export interface UseAudioRecorderOptions {
  /** Called once with the recorded clip when recording stops (not on cancel). */
  onComplete: (blob: Blob, mimeType: string) => void;
  /** Hard cap on a recording; auto-stops at this point. */
  maxDurationMs?: number;
  audioBitsPerSecond?: number;
}

export interface UseAudioRecorder {
  status: RecorderStatus;
  /** The distinct failure the UI shows a message + recovery for, or `null`. */
  error: RecorderErrorKind | null;
  /** Milliseconds left before the auto-stop cap (drives the countdown). */
  remainingMs: number;
  /** Request the mic (if needed) and start recording. */
  start: () => Promise<void>;
  /** Stop and emit the clip via `onComplete`. */
  stop: () => void;
  /** Clear any error and return to idle. */
  reset: () => void;
}

const TICK_MS = 100;

/**
 * Records a short microphone clip for voice ordering (KAN-143), via the native
 * `MediaRecorder`. Owns the getUserMedia permission request, the 60s auto-stop
 * cap + elapsed timer, and the distinct failure states the UI recovers from
 * (permission denied/dismissed, no mic, unsupported browser).
 *
 * The recorded blob is handed to `onComplete` and never retained by the hook -
 * the audio is never persisted anywhere (KAN-143). The recorder stops the
 * media-stream tracks (releasing the mic / clearing the browser's recording
 * indicator) on stop and on unmount.
 */
export function useAudioRecorder({
  onComplete,
  maxDurationMs = VOICE_ORDER_MAX_DURATION_MS,
  audioBitsPerSecond = VOICE_ORDER_AUDIO_BITS_PER_SECOND
}: UseAudioRecorderOptions): UseAudioRecorder {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [error, setError] = useState<RecorderErrorKind | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const cancelledRef = useRef(false);
  const startedAtRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep the latest callback without re-subscribing the recorder's `onstop`.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stop = useCallback(() => {
    // `stop()` triggers the recorder's `onstop`, which emits the blob and
    // releases the stream. Guard against a double stop.
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setStatus("idle");
    setElapsedMs(0);
  }, []);

  const start = useCallback(async () => {
    if (status !== "idle") {
      return;
    }
    setError(null);
    setElapsedMs(0);

    if (!isRecordingSupported()) {
      setError("unsupported");
      return;
    }

    setStatus("requesting-permission");

    let stream: MediaStream;
    try {
      // Prefer a single (mono) channel - voice-optimized and smaller. `ideal`
      // keeps it a soft hint so a device that can't do mono still records
      // rather than throwing OverconstrainedError.
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: { ideal: 1 } }
      });
    } catch (err) {
      setError(classifyGetUserMediaError(err));
      setStatus("idle");
      return;
    }

    const mimeType = pickAudioMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        audioBitsPerSecond,
        ...(mimeType ? { mimeType } : {})
      });
    } catch {
      // e.g. the picked mime type is unexpectedly rejected - release the mic
      // we just opened and surface a recoverable error rather than leaking it.
      stream.getTracks().forEach((track) => track.stop());
      setError("recording-unavailable");
      setStatus("idle");
      return;
    }

    cancelledRef.current = false;
    chunksRef.current = [];
    streamRef.current = stream;
    recorderRef.current = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      clearTimer();
      const type = recorder.mimeType || mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      releaseStream();
      recorderRef.current = null;
      setStatus("idle");
      setElapsedMs(0);
      if (!cancelledRef.current) {
        onCompleteRef.current(blob, type);
      }
    };

    startedAtRef.current = performance.now();
    // A timeslice keeps `ondataavailable` firing during the recording, so a clip
    // is never lost if `onstop` and the final chunk race on some browsers.
    recorder.start(TICK_MS);
    setStatus("recording");

    intervalRef.current = setInterval(() => {
      const elapsed = performance.now() - startedAtRef.current;
      setElapsedMs(elapsed);
      if (elapsed >= maxDurationMs) {
        stop();
      }
    }, TICK_MS);
  }, [
    status,
    audioBitsPerSecond,
    maxDurationMs,
    clearTimer,
    releaseStream,
    stop
  ]);

  // Release the mic and timers if the component unmounts mid-recording.
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearTimer();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [clearTimer]);

  const remainingMs = Math.max(0, maxDurationMs - elapsedMs);

  return {
    status,
    error,
    remainingMs,
    start,
    stop,
    reset
  };
}
